import APIError from '../errors/APIError';
import HttpClient from './utils/HttpClient';

import {
  IGroup,
  IStock,
  IProduct,
  IComplement,
  IProductPrice,
  IComplementItem,
  IUnitOfMeasurement,
} from '../models';

export type Product = Pick<
  IProduct,
  'id' | 'code' | 'gtin' | 'name' | 'order' | 'pauseSale' | 'image' | 'grids'
> & {
  price?: Pick<IProductPrice, 'value'>;
  stock?: Pick<IStock, 'minimumStock' | 'currentStock'>;
  numOfGrids: number;
  minGridsPrice?: Pick<IProductPrice, 'value'>;
  unitOfMeasurement: Pick<IUnitOfMeasurement, 'acronym'>;
};

export type Group = Pick<
  IGroup,
  'id' | 'order' | 'name' | 'pauseSale' | 'type'
> & {
  products?: Array<Product>;
};

type ShowGroupResponse = IGroup & {
  complements?: Array<
    IComplement & {
      items: Array<
        IComplementItem & {
          price: IProductPrice;
        }
      >;
    }
  >;
};

class GroupService {
  private httpClient = HttpClient;

  public async showGroup(
    companyId: number,
    groupId: number,
  ): Promise<ShowGroupResponse> {
    try {
      const response = await this.httpClient.get(
        `/companies/${companyId}/groups/${groupId}`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar obter o grupo';
      }

      throw new APIError(errorMessage);
    }
  }

  public async listGroups(
    companyId: number,
    includeProducts: boolean,
  ): Promise<Group[]> {
    try {
      const response = await this.httpClient.get(
        `/companies/${companyId}/groups?include_products=${includeProducts}`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar listar os grupos';
      }

      throw new APIError(errorMessage);
    }
  }

  public async createGroup(companyId: number, data: FormData): Promise<IGroup> {
    try {
      const response = await this.httpClient.post(
        `/companies/${companyId}/groups`,
        data,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar criar o grupo';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editGroup(
    companyId: number,
    groupId: number,
    data: FormData,
  ): Promise<IGroup> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/${groupId}`,
        data,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar editar o grupo';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editGroupStatus(
    companyId: number,
    groupId: number,
    pauseSale: boolean,
  ): Promise<IGroup> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/${groupId}/status`,
        { pauseSale },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = `Erro ao tentar ${
          pauseSale ? 'pausar' : 'ativar'
        } a venda do grupo`;
      }

      throw new APIError(errorMessage);
    }
  }

  public async sortGroups(
    companyId: number,
    groups: Pick<IGroup, 'id' | 'order'>[],
  ): Promise<IGroup[]> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/sort`,
        { groups },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = `Erro ao tentar reordenar os grupos`;
      }

      throw new APIError(errorMessage);
    }
  }

  public async deleteGroup(companyId: number, groupId: number): Promise<void> {
    try {
      await this.httpClient.delete(`/companies/${companyId}/groups/${groupId}`);
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar excluir o grupo';
      }

      throw new APIError(errorMessage);
    }
  }
}

export default new GroupService();
